// ----------------------------------------------------------------------
// <copyright file="Izenda.ExportReportAction.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var compose = require('recompose').compose;
  var withHandlers = require('recompose').withHandlers;
  var ExportFormat = require('logic/data/Izenda.ExportFormatConstants');
  var exportUtils = require('share/utils/Izenda.ExportUtils');
  var subReportLink = require('share/utils/Izenda.SubReportLink');
  var ReportUtils = require('logic/models/Izenda.ReportUtils');
  const { get, getOr, uniqueId, curry, omit, isNil, filter } = require('lodash/fp');

  var ExportButtonAction = compose(
    withHandlers({
      exportReport: curry(exportReport),
      exportDashboard: curry(exportDashboard),
      getExportFormats: curry(getExportFormats)
    })
  );

  /**
   * Get info of export loading
   */
  function getInfoExportLoading(format, name, key, isReport) {
    const formatType = exportUtils.getFileTypeByFormat(format, isReport);
    let exportName, isPrinting;

    if (format === ExportFormat.HTML.value) {
      exportName = name;
      isPrinting = true;
    } else {
      exportName = `"${name}.${formatType}"`;
    }

    return {
      isPrinting,
      name: exportName,
      key,
      error: false
    };
  }

  /*
   * Call export dashboard by format
   * @param: {object} props   React props
   * @param: {string} format  Export type
   * @param: {event}  event   React event
   */
  function exportDashboard(props, format, event) {
    var name = props.name;
    var exportLoader = props.exportLoader;
    var key = uniqueId(name);
    var id = props.id;

    exportLoader.startExportLoading(getInfoExportLoading(format, name, key, false));
    var isDesignerMode = props.isDesignerMode;
    if (isDesignerMode) {
      props.store.saveDraftDataForPrint().then(function(draftId) {
        return props.export(
          draftId,
          format,
          exportLoader.endExportLoading.bind(exportLoader, key),
          exportLoader.updateError.bind(exportLoader, key)
        );
      });
    } else {
      return props.export(
        id,
        format,
        exportLoader.endExportLoading.bind(exportLoader, key),
        exportLoader.updateError.bind(exportLoader, key)
      );
    }
  }

  /*
   * Call export report by format
   * @param: {object} props   React props
   * @param: {string} format  Export type
   * @param: {event}  event   React event
   */
  function exportReport(props, format, event) {
    var name = props.name;
    var exportLoader = props.exportLoader;
    var key = uniqueId(name);

    var store = props.store;
    var id = props.id;
    var designerStore = props.designerStore;

    function saveDraft() {
      if (props.designerStore) {
        return props.designerStore
          .savePrintDraft()
          .get('id')
          .then(function(draftId) {
            var subReportFilter = get('model.subReportFilter', props.designerStore);
            var subReportFilterFromQuery = getOr({}, 'model.subReportFilterFromQuery', props.designerStore);
            var filters = getOr([], 'filters', subReportFilter);
            var query = subReportLink.convertFilterToQuery(
              omit(['success', 'overideFilterValue'], subReportFilterFromQuery)
            );

            return {
              draftId: draftId,
              subReportFilter: filters,
              queryString: $izenda.param(query || {})
            };
          });
      }

      return Promise.resolve(null);
    }

    function exportFn(exportObj) {
      exportLoader.startExportLoading(getInfoExportLoading(format, name, key, true));
      var url = window.location.href;
      var actionFn =
        format === ExportFormat.HTML.value ? props.print.bind(props.store) : props.export.bind(props.store);

      return actionFn(
        url,
        format,
        props.id,
        props.filters,
        exportObj,
        exportLoader.endExportLoading.bind(exportLoader, key),
        exportLoader.updateError.bind(exportLoader, key)
      );
    }

    function beginExport() {
      return saveDraft().then(exportFn);
    }

    function checkFiltersAndExport() {
      if (format === ExportFormat.Query.value || format === ExportFormat.Definition.value) {
        return beginExport();
      }
      var errorMessage = props.localization.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_REQUIRED_FILTER_EXPORT');
      return ReportUtils.checkRequiredFilters(designerStore, props.id).then(function(isValidFilter) {
        if (isValidFilter) {
          return beginExport();
        } else {
          if (isNil(designerStore)) {
            props.confirmMessage('error', errorMessage);
          } else {
            designerStore.alertDetails.set({
              success: false,
              errors: [{ message: errorMessage }]
            });
          }
        }
        return false;
      });
    }

    if (store.checkValidReportId) {
      return store.checkValidReportId(id).then(function(result) {
        if (!result.success) {
          var message = result.messages[0] || {};
          var errorMessage = message.messages[0] || '';

          return props.confirmMessage('error', errorMessage);
        }
        return checkFiltersAndExport();
      });
    }

    return checkFiltersAndExport();
  }

  /**
   * Get export format options
   * @param: {object} props   React props
   * @param: {object} event   React event handle
   */
  function getExportFormats(props, event) {
    var authorization = props.authorization;
    var permissionKeys = props.authorizationConstants.PERMISSION_KEY;

    var hasWordPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatWord]);
    var hasExcelPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatExcel]);
    var hasPdfPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatPdf]);
    var hasCsvPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatCsv]);
    var hasXmlPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatXml]);
    var hasJsonPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatJson]);
    var hasQueryPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatQueryExecution]);
    var hasDefinitionPrivilege = authorization.checkPrivileges([permissionKeys.ExportingExportingFormatDefinition]);

    if (props.reportDesignerMode) {
      return filter(function(format) {
        return hasQueryPrivilege && format.value === 'query' && format.value !== ExportFormat.HTML.value;
      }, ExportFormat);
    }

    return filter(function(format) {
      return (
        format.value !== ExportFormat.HTML.value &&
        (hasWordPrivilege || format.value !== 'word') &&
        (hasExcelPrivilege || format.value !== 'excel') &&
        (hasPdfPrivilege || format.value !== 'pdf') &&
        (hasCsvPrivilege || format.value !== 'csv') &&
        (hasXmlPrivilege || format.value !== 'xml') &&
        (hasJsonPrivilege || format.value !== 'json') &&
        ((hasQueryPrivilege && !props.isDashboard) || format.value !== 'query') &&
        (hasDefinitionPrivilege || format.value !== 'definition')
      );
    }, ExportFormat);
  }

  module.exports = ExportButtonAction;
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.ExportButtonAction.jsx